import { MarketplaceTutorThemeContext } from "#/src/hooks/marketplaceTutorThemeContext";
import {
  getMarketplaceTutorThemeState,
  getMarketplaceTutorThemeUserType,
} from "#/src/hooks/marketplaceTutorThemeUtils";
import { useSessionQuery } from "#/src/hooks/useSession";
import { DesignSystemVariables } from "@web-monorepo/dds";
import { EventMetadataContext } from "@web-monorepo/product-events/EventMetadata";
import { useLayoutEffect, useMemo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

export function MarketplaceTutorThemeProvider({
  children,
  forceTutorTheme = false,
}: {
  children: ReactNode;
  forceTutorTheme?: boolean;
}) {
  const { pathname } = useLocation();
  const { sessionData } = useSessionQuery();
  const userType = getMarketplaceTutorThemeUserType(sessionData?.roles);

  const state = useMemo(
    () =>
      getMarketplaceTutorThemeState({
        pathname,
        userType,
        forceTutorTheme,
      }),
    [forceTutorTheme, pathname, userType],
  );

  useLayoutEffect(() => {
    const previousTokens = document.documentElement.getAttribute("data-dds-tokens");

    if (state.isTutorTheme) {
      document.documentElement.removeAttribute("data-dds-tokens");
    } else {
      document.documentElement.setAttribute("data-dds-tokens", "legacy");
    }

    return () => {
      // This provider is mounted once at the app root; restoring the prior value
      // avoids leaving the document tokens unset during effect re-runs.
      if (previousTokens == null) {
        document.documentElement.removeAttribute("data-dds-tokens");
      } else {
        document.documentElement.setAttribute("data-dds-tokens", previousTokens);
      }
    };
  }, [state.isTutorTheme]);

  return (
    <MarketplaceTutorThemeContext.Provider value={state}>
      <EventMetadataContext.Provider value={state.analyticsMetadata}>
        <DesignSystemVariables theme="light" brand={state.brand} />
        {children}
      </EventMetadataContext.Provider>
    </MarketplaceTutorThemeContext.Provider>
  );
}
